import React, { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { SiteConfig } from "../interfaces/ConfigInterface";

/**
 * New Employee Sync — v2 modal (Employees page).
 * Rendered inside the page's `.emp` scope, so it reuses the shared
 * emp-modal / emp-to-* classes from EmployeeView.css.
 * The legacy EmployeeSyncModal remains for the v1 page (EmployeeViewV1).
 */
interface EmployeeSyncModalV2Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (employeeAltID: string, employeeAltLocationID: string) => void;
    config: SiteConfig;
}

const EmployeeSyncModalV2: React.FC<EmployeeSyncModalV2Props> = ({ isOpen, onClose, onSubmit, config }) => {
    const [employeeAltID, setEmployeeAltID] = useState("");
    const [employeeAltLocationID, setEmployeeAltLocationID] = useState("");

    // Fresh fields on every open (the legacy modal kept stale values around).
    useEffect(() => {
        if (isOpen) {
            setEmployeeAltID("");
            setEmployeeAltLocationID("");
        }
    }, [isOpen]);

    // Esc closes (matching the page's other v2 modals).
    useEffect(() => {
        if (!isOpen) return;
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const needsCompany = !!config.employeeSyncShowCompany;
    const canSubmit = employeeAltID.trim() !== "" && (!needsCompany || employeeAltLocationID.trim() !== "");

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit(employeeAltID.trim(), needsCompany ? employeeAltLocationID.trim() : "");
        onClose();
    };

    return (
        <div className="emp-modal-bg" onClick={onClose}>
            <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="emp-modal-head">
                    <div className="mi">
                        <RefreshCw size={21} />
                    </div>
                    <div>
                        <h3>New Employee Sync</h3>
                        <div className="ms">Pull the latest employee data from payroll by ID</div>
                    </div>
                    <button type="button" className="x" onClick={onClose}>
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Body */}
                    <div className="emp-modal-body">
                        <div className="emp-to-frow">
                            <label>
                                Employee ID <span className="emp-req">*</span>
                            </label>
                            <input
                                className="emp-input"
                                type="text"
                                value={employeeAltID}
                                onChange={(e) => setEmployeeAltID(e.target.value)}
                                autoFocus
                            />
                            <div className="emp-to-help">The employee&apos;s number in the payroll system.</div>
                        </div>
                        {needsCompany && (
                            <div className="emp-to-frow">
                                <label>
                                    Company ID <span className="emp-req">*</span>
                                </label>
                                <input
                                    className="emp-input"
                                    type="text"
                                    value={employeeAltLocationID}
                                    onChange={(e) => setEmployeeAltLocationID(e.target.value)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="emp-modal-foot">
                        <button type="button" className="emp-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="emp-btn primary" disabled={!canSubmit}>
                            <RefreshCw size={16} />
                            Sync
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EmployeeSyncModalV2;
